/**
 * Preparing a photo for upload.
 *
 * A phone camera produces 4–12 MB files at 4000px wide. Nothing on this site
 * shows a picture wider than about 900 CSS pixels, so uploading the original
 * costs storage, costs the customer their data allowance on a phone, and shows
 * them nothing extra. Everything is shrunk in the browser before it is sent.
 *
 * The pure parts live here so they can be tested without a canvas.
 */

/** Matches the cap in storage.rules. Checked here only to fail fast and kindly. */
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024

/** Also matches storage.rules. A file outside this list is refused by the server too. */
export const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'] as const

/** Longest edge after shrinking. Twice the largest display size, for hi-dpi screens. */
export const MAX_EDGE = 1600

export const MAX_PHOTOS = 8

export function isAcceptedType(type: string | undefined): boolean {
  return (ACCEPTED_TYPES as readonly string[]).includes((type || '').toLowerCase())
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${Math.round(bytes)} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Why a file cannot be used, in words the person can act on, or null if it can.
 * iPhone HEIC gets its own message because "wrong file type" is useless advice
 * when the fix is a setting in the camera app.
 */
export function rejectionReason(file: { name?: string; type?: string; size?: number }): string | null {
  const type = (file.type || '').toLowerCase()
  const name = (file.name || '').toLowerCase()

  if (type === 'image/heic' || type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif')) {
    return 'iPhone HEIC photos are not supported. In Settings → Camera → Formats choose "Most Compatible", or share the photo to yourself first to turn it into a JPEG.'
  }
  if (!isAcceptedType(type)) return 'That is not a photo. Use a JPEG, PNG or WebP.'
  if ((file.size ?? 0) > MAX_UPLOAD_BYTES) {
    return `That file is ${formatBytes(file.size ?? 0)}. The limit is ${formatBytes(MAX_UPLOAD_BYTES)}.`
  }
  if ((file.size ?? 0) === 0) return 'That file is empty.'
  return null
}

/** Fits inside a square of maxEdge, keeping the shape. Never enlarges. */
export function targetSize(
  width: number, height: number, maxEdge = MAX_EDGE,
): { width: number; height: number } {
  const w = Math.max(1, Math.round(width))
  const h = Math.max(1, Math.round(height))
  const longest = Math.max(w, h)
  if (longest <= maxEdge) return { width: w, height: h }
  const scale = maxEdge / longest
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) }
}

/** A collision-proof path under the prefix the Storage rules allow. */
export function photoPath(folder: string, ownerId: string, originalName = ''): string {
  const ext = /\.(jpe?g|png|webp|avif)$/i.exec(originalName)?.[1]?.toLowerCase() ?? 'webp'
  const safeOwner = (ownerId || 'new').replace(/[^A-Za-z0-9_-]/g, '') || 'new'
  const stamp = Date.now().toString(36)
  const rand = Math.random().toString(36).slice(2, 8)
  return `stock/${folder}/${safeOwner}/${stamp}-${rand}.${ext === 'jpeg' ? 'jpg' : ext}`
}

/* --------------------------- the canvas part --------------------------- */

function loadBitmap(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That image could not be read.')) }
    img.src = url
  })
}

/**
 * Shrinks to MAX_EDGE and re-encodes as WebP. Returns the original untouched if
 * anything goes wrong — a photo that uploads large beats a photo that does not
 * upload at all.
 */
export async function shrink(
  file: File, maxEdge = MAX_EDGE,
): Promise<{ blob: Blob; type: string; ext: string; resized: boolean }> {
  const keep = { blob: file as Blob, type: file.type, ext: file.name, resized: false }
  try {
    const img = await loadBitmap(file)
    const size = targetSize(img.naturalWidth, img.naturalHeight, maxEdge)
    if (size.width === img.naturalWidth && size.height === img.naturalHeight && file.size < 900_000) {
      return keep
    }
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const ctx = canvas.getContext('2d')
    if (!ctx) return keep
    ctx.drawImage(img, 0, 0, size.width, size.height)

    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/webp', 0.85))
    // A browser that cannot encode WebP hands back null or a PNG; either way the
    // original is the safer thing to send.
    if (!blob || blob.type !== 'image/webp' || blob.size >= file.size) return keep
    return { blob, type: 'image/webp', ext: 'photo.webp', resized: true }
  } catch {
    return keep
  }
}
